import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { i18n } from '../../i18n/i18n';
import { ludsElevation } from '../../shared/styles/elevation';
import { ludsBaseFont } from '../../shared/styles/typography';
import { debounce } from '../../shared/util/debounce';
import '../button/button';
import '../dropodown-menu/dropdown-menu';
import '../icon/icon';
import { lu_screen_s } from '../../shared/styles/breakpoint';

export interface BreadcrumbItem {
  id: string | number;
  itemName: string;
  path?: string;
  onClick?: (e: Event) => void;
}

/**
 * LudsDropdownMenu component
 */
@customElement('luds-breadcrumbs')
export class LudsBreadcrumbs extends LitElement {
  static override styles = [
    ludsBaseFont,
    ludsElevation,
    css`
      :host {
        --bc-link-color: var(--semantic-colour-tokens-text-text-brand);
        --bc-link-non-clickable-color: var(--semantic-colour-tokens-text-text-secondary);
        --bc-link-border: var(--semantic-colour-tokens-stroke-stroke-brand);
        --bc-link-shadow: var(--colours-brand-primary);
        --gap-4: var(--spacing-spacing-04);

        --bc-lh: 16px;
        --bc-fs: 12px;

        display: block;
      }

      .breadcrumb {
        position: relative;
        z-index: 1;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--gap-4);
        list-style: none;
        margin: 0;
        padding: 0;
        width: 100%;
        transition: all 0.3s;
      }

      .breadcrumb__item {
        display: flex;
        align-items: center;
        gap: var(--gap-4);
      }

      .breadcrumb__link {
        color: var(--bc-link-color);
        font-weight: 500;
        line-height: var(--bc-lh);
        font-size: var(--bc-fs);
        text-decoration: none;
        padding: var(--spacing-spacing-04) var(--spacing-spacing-02);
        transition: all 0.3s;
        border-radius: var(--corners-s, 4px);
        outline: none;
        border: 2px solid transparent;

        &:focus {
          border: 2px solid var(--bc-link-border);
          box-shadow: var(--luds-elevetion-4);
          text-decoration: underline;
        }
      }

      .breadcrumb__link--non-link {
        color: var(--bc-link-non-clickable-color);
      }

      .lh-100 {
        line-height: 100%;
      }

      .hidden {
        display: none;
      }

      .block {
        display: block;
      }

      .ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      @media (max-width: ${lu_screen_s}px) {
        .ellipsis {
          width: 250px;
        }
      }
    `,
  ];

  @property({ type: Array }) items: BreadcrumbItem[] = [];
  @property({ type: Object }) homeItem: BreadcrumbItem;

  // Adding items as visible state
  @state() visibleItems: typeof this.items = [...this.items];
  @state() hiddenItems: typeof this.items = [];

  // Add queries for breadcumrbs
  @query('.breadcrumb') breadcrumb!: HTMLElement;
  currentWidth = 0;

  private renderBreadcrumbsItems(isLastItem: boolean, item: BreadcrumbItem) {
    return isLastItem
      ? html`<span class="breadcrumb__link breadcrumb__link--non-link ellipsis">${item.itemName}</span>`
      : html`<a href="${ifDefined(item.path)}" class="breadcrumb__link" @click="${item.onClick}">${item.itemName}</a>`;
  }

  private renderBreadcrumbsItemSeparator(isNotLastItem: boolean) {
    return isNotLastItem
      ? html`<luds-icon
          aria-hidden="true"
          icon="chevron_right"
          size="extra-small"
          color="var(--semantic-colour-tokens-icons-icon-default)"></luds-icon>`
      : nothing;
  }

  /**
   * Calculates which breadcrumbs should be visible and which should be hidden in a dropdown menu
   * based on the available space in the container.
   */
  private updateVisibleBreadcrumbs() {
    if (!this.breadcrumb) return;

    this.breadcrumb.style.setProperty('opacity', '0');
    const GAP = parseInt(getComputedStyle(this.breadcrumb).gap ?? '0', 10);
    const BREADCRUMB_SPACING_BUFFER = 50;
    const ITEMS_WITHOUT_LAST = this.items.slice(0, -1);

    // Calculate 75% of available width minus padding as the maximum allowed width
    const allowedWidth = this.breadcrumb.getBoundingClientRect().width * 0.75 - BREADCRUMB_SPACING_BUFFER;

    // Start with the width of the last item (which is always shown)
    let total = this.breadcrumb.lastElementChild.getBoundingClientRect().width;
    let count = 0;

    for (const item of this.breadcrumb.children) {
      // Add each item's width plus the gap to the running total
      total += item.getBoundingClientRect().width + GAP;

      // If we're still within the allowed width, increment the counter
      if (total <= allowedWidth) {
        count++;
      } else {
        // If we've exceeded the allowed width, step back one item
        if (count > 0) {
          count--;
        }
      }
    }

    // Split array by index, from the left to visibleItems from the right to hiddenItems
    this.visibleItems = ITEMS_WITHOUT_LAST.slice(0, count);
    this.hiddenItems = ITEMS_WITHOUT_LAST.slice(count);

    this.breadcrumb.style.setProperty('opacity', '1');
  }

  observer = new ResizeObserver(
    debounce((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (this.currentWidth != entry.contentRect.width) {
        this.currentWidth = entry.contentRect.width;
        this.updateVisibleBreadcrumbs();
      }
    }, 50)
  );

  override firstUpdated(): void {
    requestIdleCallback(() => {
      this.updateVisibleBreadcrumbs();
    });

    if (this.breadcrumb) this.observer.observe(this.breadcrumb);
  }

  override disconnectedCallback() {
    if (this.breadcrumb) this.observer.unobserve(this.breadcrumb);
    super.disconnectedCallback();
  }

  /**
   * Ensures breadcrumbs recalculate immediately when items or homeItem change.
   * Without this, breadcrumbs only update after a window resize or initial render,
   * causing delayed or incorrect display.
   */
  override updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);
    if (changedProps.has('items') || changedProps.has('homeItem')) {
      this.updateVisibleBreadcrumbs();
    }
  }

  override render() {
    const { visibleItems, hiddenItems, items, homeItem } = this;

    const dropdownMenuRef = createRef();
    let isOpen = false;

    const toggleMenu = () => {
      isOpen = !isOpen;
      const dropdownMenu = dropdownMenuRef.value as HTMLElement & { isOpen: boolean };
      dropdownMenu.isOpen = isOpen;
    };

    return html`<ul class="breadcrumb">
      ${homeItem
        ? html`<li class="breadcrumb__item">
            <a
              href="${ifDefined(homeItem.path)}"
              class="breadcrumb__link lh-100"
              aria-label="${i18n('lua.crubs.home')}"
              @click="${homeItem.onClick}">
              <luds-icon
                icon="home"
                size="small"
                color="var(--semantic-colour-tokens-icons-icon-default)"
                aria-hidden="true"></luds-icon>
            </a>

            ${items.length
              ? html`
            <luds-icon
              aria-hidden="true"
              icon="chevron_right"
              size="extra-small"
              color="var(--semantic-colour-tokens-icons-icon-default)"></luds-icon>
          </li>`
              : nothing}
          </li> `
        : nothing}
      ${items.length
        ? html`
            ${repeat(
              visibleItems,
              (item) => item.id,
              (item) => {
                return html`
                  <li class="breadcrumb__item">
                    ${this.renderBreadcrumbsItems(false, item)} ${this.renderBreadcrumbsItemSeparator(true)}
                  </li>
                `;
              }
            )}
            ${hiddenItems.length > 0
              ? html`<li class="breadcrumb__item">
                  <div>
                    <luds-button
                      variant="flat"
                      size="large"
                      icon="skewer"
                      .a11yAriaHasPopup="${true}"
                      .a11yAriaExpanded="${isOpen}"
                      a11yAriaControls="dropdown-menu"
                      a11yAriaLabel=${i18n('lua.a11y.3dot_menu_btn_title')}
                      @click="${toggleMenu}">
                    </luds-button>

                    <luds-dropdown-menu
                      ${ref(dropdownMenuRef)}
                      .isOpen="${false}"
                      id="dropdown-menu"
                      .dropdownMenuItems="${hiddenItems}"
                      displayAs="itemName"></luds-dropdown-menu>
                  </div>
                  ${this.renderBreadcrumbsItemSeparator(true)}
                </li>`
              : nothing}

            <li class="breadcrumb__item ellipsis" id="lastItem">
              ${this.renderBreadcrumbsItems(true, items[items.length - 1])}
            </li>
          `
        : nothing}
    </ul>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-breadcrumbs': LudsBreadcrumbs;
  }
}
